/**
 * @yjsgames/boot — 도메인 부트스트래퍼(설치자) 최소 계약 (순수 TS).
 *
 * 게임 씬/앱 조립을 도메인 수직 슬라이스로 분할한다: 각 설치자가 자기 도메인의
 * 생성·결선·등록을 install() 한 번으로 포장하고, 호출자는 순서 배열(SSoT)을 실행한다.
 * 동기 실행 전용 — 비동기 부팅 단계/마커(race 해결 장치)는 본 패키지 범위 밖.
 *
 * TBoot = 호출자가 정의하는 조립 컨텍스트(씬·컨트롤러 접근 창구·레지스트리 등).
 * 패키지는 도메인/엔진 타입을 모른다 (DIP — 제네릭 파라미터만).
 */
export interface Bootstrapper<TBoot> {
  /** 도메인 식별자 — 시퀀스 내 유일해야 한다 (중복은 runBootSequence가 즉시 throw). */
  readonly key: string;
  install(boot: TBoot): void;
}

export interface RunBootOptions {
  /** key별 활성 여부 (featureFlags 게이트 등). false면 그 설치자는 생략. 미지정 시 전부 활성. */
  isEnabled?: (key: string) => boolean;
}

/**
 * 설치자 배열을 선언 순서대로 실행한다.
 * @returns 실제 설치된 key 목록 (isEnabled로 생략된 항목 제외) — 부팅 로그/검증용.
 * @throws 같은 key가 두 번 등장하면 즉시 throw (조립 구성 오류는 조기 실패가 정답).
 */
export function runBootSequence<TBoot>(
  boot: TBoot,
  sequence: readonly Bootstrapper<TBoot>[],
  options: RunBootOptions = {},
): string[] {
  const seen = new Set<string>();
  const installed: string[] = [];
  for (const b of sequence) {
    if (seen.has(b.key)) {
      throw new Error(`[boot] 중복 부트스트래퍼 key: ${b.key}`);
    }
    seen.add(b.key);
    if (options.isEnabled && !options.isEnabled(b.key)) continue;
    b.install(boot);
    installed.push(b.key);
  }
  return installed;
}

/**
 * 해제 수집기 — install↔dispose 대칭 (boot 패러다임 v2).
 *
 * 호출자가 TBoot에 `addDisposer: bag.add`를 실어 주면, 각 설치자가 자기 도메인의 해제
 * (타이머 정리·리스너 제거·뷰 destroy)를 설치 시점에 등록한다. 호스트(씬 SHUTDOWN 등)는
 * disposeAll() 한 번으로 **등록 역순** 일괄 해제 — 설치 순서 의존(나중 설치가 먼저 해제)을 보존한다.
 * 패키지는 여전히 도메인/엔진을 모른다 (순수 함수 콜백만).
 */
export interface DisposerBag {
  /** 해제 콜백 등록 — 설치자 install() 안에서 호출. */
  add(fn: () => void): void;
  /** 등록 역순 일괄 해제 + 목록 비움 (중복 호출 무해). */
  disposeAll(): void;
}

export function createDisposerBag(): DisposerBag {
  const fns: (() => void)[] = [];
  return {
    add(fn) {
      fns.push(fn);
    },
    disposeAll() {
      for (let i = fns.length - 1; i >= 0; i--) fns[i]!();
      fns.length = 0;
    },
  };
}
